import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import {
    collection, doc, query, orderBy, limit, startAfter,
    onSnapshot, getDocs, updateDoc, deleteDoc, writeBatch,
    QueryDocumentSnapshot, DocumentData,
} from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { db } from '../../providers/firebase/firebase';
import { normalizeNotificationDoc, type NotificationDoc } from '../../types/notifications';

interface UseNotificationsOpts {
    pageSize?: number;
}

interface UseNotificationsResult {
    notifications: NotificationDoc[];
    unreadCount: number;
    hasMore: boolean;
    loading: boolean;
    loadMore: () => Promise<void>;
    markRead: (notifId: string) => Promise<void>;
    markAllRead: () => Promise<void>;
    dismiss: (notifId: string) => Promise<void>;
}

export function useNotifications(opts: UseNotificationsOpts = {}): UseNotificationsResult {
    const pageSize = opts.pageSize ?? 20;
    const [uid, setUid] = useState<string | null>(null);
    const [notifications, setNotifications] = useState<NotificationDoc[]>([]);
    const [loading, setLoading] = useState(true);
    const [hasMore, setHasMore] = useState(true);
    const lastDocRef = useRef<QueryDocumentSnapshot<DocumentData> | null>(null);

    useEffect(() => {
        const auth = getAuth();
        if (auth.currentUser) setUid(auth.currentUser.uid);
        return onAuthStateChanged(auth, u => setUid(u?.uid ?? null));
    }, []);

    useEffect(() => {
        if (!uid) return;
        const q = query(
            collection(db, `users/${uid}/notifications`),
            orderBy('delivered_at', 'desc'),
            limit(pageSize),
        );
        const unsub = onSnapshot(q, snap => {
            const docs = snap.docs.map(d => normalizeNotificationDoc(d.data()));
            setNotifications(docs);
            lastDocRef.current = snap.docs[snap.docs.length - 1] ?? null;
            setHasMore(snap.docs.length === pageSize);
            setLoading(false);
        });
        return () => unsub();
    }, [uid, pageSize]);

    const unreadCount = useMemo(
        () => notifications.filter(n => !n.read).length,
        [notifications],
    );

    const loadMore = useCallback(async () => {
        if (!uid || !lastDocRef.current) return;
        const q = query(
            collection(db, `users/${uid}/notifications`),
            orderBy('delivered_at', 'desc'),
            startAfter(lastDocRef.current),
            limit(pageSize),
        );
        const snap = await getDocs(q);
        const more = snap.docs.map(d => normalizeNotificationDoc(d.data()));
        setNotifications(prev => [...prev, ...more]);
        lastDocRef.current = snap.docs[snap.docs.length - 1] ?? lastDocRef.current;
        setHasMore(snap.docs.length === pageSize);
    }, [uid, pageSize]);

    const markRead = useCallback(async (notifId: string) => {
        if (!uid) return;
        await updateDoc(doc(db, `users/${uid}/notifications/${notifId}`), { read: true });
    }, [uid]);

    const markAllRead = useCallback(async () => {
        if (!uid) return;
        const batch = writeBatch(db);
        for (const n of notifications) {
            if (!n.read) {
                batch.update(doc(db, `users/${uid}/notifications/${n.notif_id}`), { read: true });
            }
        }
        await batch.commit();
    }, [uid, notifications]);

    const dismiss = useCallback(async (notifId: string) => {
        if (!uid) return;
        await deleteDoc(doc(db, `users/${uid}/notifications/${notifId}`));
    }, [uid]);

    return { notifications, unreadCount, hasMore, loading, loadMore, markRead, markAllRead, dismiss };
}
